import { z } from 'zod';
import { uuidSchema } from './common.js';
/**
 * Protocole de synchronisation (§12.8).
 *
 * Écrit dans `packages/shared` parce que le client ET le serveur en dépendent :
 * la file d'attente locale construit ces objets, l'API les valide. Une seule
 * définition, donc pas de dérive silencieuse entre les deux bouts.
 */
/**
 * ⭐ LA liste des entités répliquées. Source unique.
 *
 * ⚠️ Elle a existé en QUATRE exemplaires — ici, dans le schéma d'amorçage, dans
 * le dépôt de synchronisation du serveur et dans le moteur du client — et ils
 * ont divergé : `transaction` et `tax` manquaient au schéma d'amorçage, qui
 * refusait donc de les servir. Le client réessayait indéfiniment, sans jamais
 * recevoir ni les ventes ni les taux, et aucune erreur ne remontait à l'écran.
 * Toute nouvelle entité s'ajoute ICI, et nulle part ailleurs.
 */
export const ENTITES_REPLIQUEES = [
    'customer',
    'item',
    'category',
    'supplier',
    // Répliqué en LECTURE seule : un appareil le reçoit, il ne le pousse jamais.
    'stock_level',
    // Ventes : répliquées ET poussées — encaisser hors ligne est la promesse
    // centrale du produit.
    'transaction',
    // Taux de TVA, en lecture seule : sans eux, la caisse hors ligne calculerait
    // une TVA nulle et remettrait un ticket faux.
    'tax',
    // Rendez-vous : répliqués ET poussés. Consulter l'agenda est le geste d'une
    // caissière au comptoir, au moment où le client demande un créneau — c'est-à-
    // dire exactement quand la connexion peut manquer.
    'appointment',
    // Caisse : répliquée ET poussée. Sans elle, l'appareil ne saurait pas si le
    // tiroir est ouvert, et le verrou « pas de vente sans caisse » se
    // retournerait contre le commerçant à la première coupure réseau — il
    // bloquerait précisément là où le produit promet de tenir.
    'cash_session',
    'cash_movement',
    // Blocages de réservation : répliqués en LECTURE seule. La grille de l'agenda
    // doit pouvoir dessiner les places fermées sans réseau — sans quoi le
    // comptoir hors ligne poserait un rendez-vous sur une place administrativement
    // close, et ne l'apprendrait qu'au retour de la connexion, par un refus.
    'blocage',
];
/** Pluriels tolérés dans les mutations : le protocole, lui, parle au singulier. */
const PLURIELS = [
    'customers',
    'items',
    'categories',
    'suppliers',
    'transactions',
    'taxes',
    'appointments',
    'cash-sessions',
    'cash-movements',
    'blocages',
];
export const syncEntitySchema = z.enum([...ENTITES_REPLIQUEES, ...PLURIELS]);
export const mutationOpSchema = z.enum(['create', 'update', 'delete']);
export const mutationSchema = z.object({
    /** UUIDv7 — sert AUSSI de clé d'idempotence (§7.6). */
    id: uuidSchema,
    entity: syncEntitySchema,
    entityId: uuidSchema,
    op: mutationOpSchema,
    /** Version connue de l'appareil au moment de l'écriture. `null` en création. */
    baseVersion: z.number().int().min(0).nullable().default(null),
    /** Charge PARTIELLE en mise à jour : seuls les champs réellement modifiés,
     *  ce qui est la condition pour pouvoir fusionner deux écritures. */
    payload: z.record(z.string(), z.unknown()).default({}),
});
/** Le lot est plafonné : un appareil resté trois jours hors ligne pousse en
 *  plusieurs fois plutôt qu'en une requête d'une minute. */
export const pushRequestSchema = z.object({
    deviceId: uuidSchema,
    mutations: z.array(mutationSchema).min(1).max(100),
});
export const changesQuerySchema = z.object({
    since: z
        .string()
        .regex(/^\d+$/, 'Curseur invalide')
        .default('0'),
    limit: z.coerce.number().int().min(1).max(500).default(500),
    deviceId: uuidSchema.optional(),
});
export const bootstrapQuerySchema = z.object({
    // Dérivé de la source unique : plus de liste à tenir à jour en double.
    entity: z.enum(ENTITES_REPLIQUEES),
    after: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(1000).default(500),
});
export const deviceInputSchema = z.object({
    id: uuidSchema,
    label: z.string().trim().max(120).optional(),
    platform: z.string().trim().max(60).optional(),
});
//# sourceMappingURL=sync.js.map